import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    Box,
    Text,
    Button,
    Checkbox,
    Alert,
    AlertIcon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {CheckCircleIcon} from '../../../components/icons'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import useDeviceDetection from '../../../hooks/use-device-detection'
import {sendViewErrorEvent} from '../../../utils/gtm-utils'
import {useBrand} from '../../../contexts/brand-context'

/**
 * Inline newsletter subscribe form rendered by the `{newsletterForm}` CMS
 * dynamic tag (see gdiTextBlock). This component renders only the consent
 * checkbox + Subscribe button — the surrounding marketing copy (title, icon,
 * supporting paragraphs) is authored in the CMS text block itself, around
 * the `{newsletterForm}` token.
 *
 * Submit logic is identical to the legacy NewsletterSubscribeDrawer:
 *   updateCustomer({c_email_garage_optedIn: true, c_device})
 *
 * On success it calls `onSuccess` (the caller wires the custom toast +
 * parent-modal close). When the customer is already subscribed it renders
 * a short success state with a "Manage preferences" link, matching the
 * behaviour of the legacy drawer.
 */
const NewsletterFormInline = ({customer, onSuccess}) => {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const justSubscribedRef = useRef(false)
    const customerId = useCustomerId()
    const device = useDeviceDetection()
    // @tanstack/react-query v4 exposes `isLoading`; v5 renamed it to `isPending`.
    // commerce-sdk-react currently pulls v4, so destructure both and combine.
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const {mutate: updateCustomer} = updateCustomerMutation
    const isLoading = updateCustomerMutation.isLoading || updateCustomerMutation.isPending

    const form = useForm({
        defaultValues: {
            consent: false
        }
    })

    const {
        register,
        handleSubmit,
        formState: {errors},
        clearErrors,
        setError
    } = form

    const isAlreadySubscribed = !!customer?.c_email_garage_optedIn

    const onSubmit = () => {
        if (!customerId) return
        updateCustomer(
            {
                parameters: {customerId},
                body: {
                    c_email_garage_optedIn: true,
                    c_device: device
                }
            },
            {
                onSuccess: () => {
                    justSubscribedRef.current = true
                    setIsSubmitted(true)
                    onSuccess?.()
                },
                onError: () => {
                    sendViewErrorEvent('Failed to subscribe to newsletter. Please try again.')
                    setError('global', {
                        type: 'manual',
                        message: isGarage
                            ? formatMessage({
                                  id: 'rewards.newsletter_subscribe.error_message.garage',
                                  defaultMessage: 'Failed to subscribe to newsletter. Please try again.'
                              })
                            : formatMessage({
                                  id: 'rewards.newsletter_subscribe.error_message.dynamite',
                                  defaultMessage: 'Failed to subscribe to newsletter. Please try again.'
                              })
                    })
                }
            }
        )
    }

    if (isSubmitted || isAlreadySubscribed) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={3}
                textAlign="center"
            >
                <CheckCircleIcon boxSize="32px" />
                <Text textStyle="bodySmRegular">
                    {isSubmitted || justSubscribedRef.current ? (
                        isGarage ? (
                            <FormattedMessage
                                id="rewards.newsletter_subscribe.success_title.garage"
                                defaultMessage="Thanks! You are officially subscribed to our newsletter & your points are on the way!"
                            />
                        ) : (
                            <FormattedMessage
                                id="rewards.newsletter_subscribe.success_title.dynamite"
                                defaultMessage="Thanks! You are officially subscribed to our newsletter & your points are on the way!"
                            />
                        )
                    ) : isGarage ? (
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.already_subscribed_title.garage"
                            defaultMessage="You're already subscribed to our newsletter."
                        />
                    ) : (
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.already_subscribed_title.dynamite"
                            defaultMessage="You're already subscribed to our newsletter."
                        />
                    )}
                </Text>
                <Link to="/account/email">
                    <FormattedMessage
                        id="rewards.newsletter_subscribe.manage_preferences"
                        defaultMessage="Manage preferences"
                    />
                </Link>
            </Box>
        )
    }

    return (
        <Box
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            display="flex"
            flexDirection="column"
            gap={4}
        >
            <Box textAlign="left">
                <Checkbox
                    {...register('consent', {
                        required: isGarage
                            ? formatMessage({
                                  id: 'rewards.newsletter_subscribe.consent_required.garage',
                                  defaultMessage:
                                      'You must agree to receive commercial messages to subscribe.'
                              })
                            : formatMessage({
                                  id: 'rewards.newsletter_subscribe.consent_required.dynamite',
                                  defaultMessage:
                                      'You must agree to receive commercial messages to subscribe.'
                              })
                    })}
                    alignItems="flex-start"
                >
                    <Text
                        as="span"
                        textStyle={"bodySmRegular"}
                        textAlign="left"
                    >
                        <FormattedMessage
                            id="rewards.newsletter_subscribe.consent_label"
                            defaultMessage="Yes, I would like to receive commercial electronic messages, including a weekly newsletter and discount notification. I understand I can unsubscribe at any time."
                        />
                    </Text>
                </Checkbox>
                {errors.consent && (
                    <Text
                        as="div"
                        mt={2}
                        textStyle={"bodyXsRegular"}
                        color="webTypo.textError"
                        textAlign="left"
                        paddingLeft={6}
                    >
                        {errors.consent.message}
                    </Text>
                )}
            </Box>

            {form.formState.errors?.global && (
                <Alert status="error">
                    <AlertIcon />
                    <Text
                        as="div"
                        fontSize="14px"
                        lineHeight="20px"
                        fontWeight="normal"
                        color="webTypo.textError"
                        textAlign="left"
                    >
                        {form.formState.errors.global.message}
                    </Text>
                </Alert>
            )}

            <Button
                type="submit"
                variant="webPrimary"
                isLoading={isLoading}
                isDisabled={isLoading}
                loadingText={formatMessage({
                    id: 'rewards.newsletter_subscribe.subscribing',
                    defaultMessage: 'Subscribing...'
                })}
                onClick={() => clearErrors('global')}
            >
                <FormattedMessage
                    id="rewards.newsletter_subscribe.cta"
                    defaultMessage="Subscribe"
                />
            </Button>
        </Box>
    )
}

NewsletterFormInline.propTypes = {
    customer: PropTypes.object,
    onSuccess: PropTypes.func
}

export default NewsletterFormInline
